import { useEffect, useState } from "react";
import { Users, Plus, Pencil, Power, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { PageHeader, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusPill } from "@/components/StatusPill";
import { api, apiErr } from "@/lib/api";

const EMPTY = { name: "", email: "", password: "", pin: "", role: "USER", location_ids: [] };

export default function Usuarios() {
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/users?include_inactive=true").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); api.get("/locations").then(({ data }) => setLocations(data)).catch(() => {}); }, []);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (u) => { setForm({ ...EMPTY, name: u.name, email: u.email || "", role: u.role, location_ids: u.location_ids || [] }); setEditId(u.id); setOpen(true); };

  const save = async () => {
    if (!form.name) { toast.error("El nombre es obligatorio"); return; }
    if (!editId && !form.email && !form.pin) { toast.error("Define un correo/contraseña o un PIN"); return; }
    const payload = { name: form.name, role: form.role, location_ids: form.location_ids };
    if (form.email) payload.email = form.email;
    if (form.password) payload.password = form.password;
    if (form.pin) payload.pin = form.pin;
    try {
      if (editId) await api.put(`/users/${editId}`, payload);
      else await api.post("/users", payload);
      toast.success("Usuario guardado"); setOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const toggleLoc = (id) => setForm((f) => ({ ...f, location_ids: f.location_ids.includes(id) ? f.location_ids.filter((x) => x !== id) : [...f.location_ids, id] }));
  const deactivate = async (u) => { await api.post(`/users/${u.id}/deactivate`); toast.success("Usuario desactivado"); load(); };

  return (
    <div>
      <PageHeader title="Usuarios" subtitle="Cada acción queda registrada por usuario individual"
        right={<Button data-testid="new-user-btn" onClick={openNew} className="rounded-xl"><Plus className="mr-2 h-4 w-4" /> Nuevo usuario</Button>} />
      {items.length === 0 ? (
        <EmptyState icon={Users} title="Sin usuarios" subtitle="Crea tu primer usuario." />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          {items.map((u) => (
            <div key={u.id} data-testid={`user-${u.id}`} className={`flex items-center gap-3 border-b border-slate-100 p-4 last:border-0 ${!u.active ? "opacity-60" : ""}`}>
              <div className="grid h-10 w-10 place-items-center rounded-full bg-primary/10 font-semibold text-primary">{u.name?.[0]}</div>
              <div className="min-w-0 flex-1">
                <p className="font-medium">{u.name}</p>
                <p className="text-xs text-slate-500">{u.email || "Acceso por PIN"} · {(u.location_ids || []).length} local(es)</p>
              </div>
              <div className="flex items-center gap-2">
                {u.has_pin && <KeyRound className="h-4 w-4 text-slate-400" title="Tiene PIN" />}
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">{u.role === "ADMIN" ? "Admin" : "Operador"}</span>
                {!u.active && <StatusPill status="READ" label="Inactivo" />}
                <Button data-testid={`edit-user-${u.id}`} size="sm" variant="ghost" onClick={() => openEdit(u)}><Pencil className="h-4 w-4" /></Button>
                {u.active && <Button size="sm" variant="ghost" onClick={() => deactivate(u)}><Power className="h-4 w-4 text-slate-400" /></Button>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display">{editId ? "Editar usuario" : "Nuevo usuario"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-sm">Nombre *</Label><Input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 h-11" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm">Rol</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role" className="mt-1 h-11"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="USER">Operador</SelectItem><SelectItem value="ADMIN">Administrador</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="text-sm">PIN (operacional)</Label><Input data-testid="user-pin" value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value })} className="mt-1 h-11" placeholder="4-8 dígitos" inputMode="numeric" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm">Correo</Label><Input data-testid="user-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 h-11" placeholder="correo@empresa.cl" /></div>
              <div><Label className="text-sm">Contraseña</Label><Input data-testid="user-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 h-11" placeholder={editId ? "(sin cambios)" : "••••••"} /></div>
            </div>
            <div>
              <Label className="text-sm">Locales asignados</Label>
              <div className="mt-2 space-y-2">
                {locations.map((l) => (
                  <label key={l.id} className="flex items-center gap-2 text-sm">
                    <Checkbox data-testid={`user-loc-${l.id}`} checked={form.location_ids.includes(l.id)} onCheckedChange={() => toggleLoc(l.id)} /> {l.name}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter><Button data-testid="save-user-btn" onClick={save} className="rounded-xl">Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
